/**
 * Per-window session token bridge for the Tenant Window Manager desktop.
 *
 * A studio SPA instance running INSIDE a tenant window (an iframe of the
 * Workspace desktop) authenticates NOT with the shared per-origin session
 * cookie — which is the shell operator's ONE live tenant — but with its OWN
 * short-lived `wtoken` minted by the shell (`mintWindowToken(tenantId, role)`).
 * The token carries the same operator identity scoped to that window's tenant +
 * assumed role. This is what isolates windows: a window switching tenant/role
 * re-mints ITS token and never touches the shell's cookie.
 *
 * The shell passes the token into the iframe URL (`/studio/?embed=workspace&
 * wtoken=<token>`), mirroring the connector-app `?__st=` precedent. This module
 * reads it ONCE at boot and stashes it in a module-level variable (Apollo links
 * and the Absinthe socket live outside the React tree, exactly like
 * `activeRoleHeader`). Each frame is its own JS context, so the shell frame
 * (no wtoken → cookie auth) and each window frame (its own wtoken → bearer
 * auth) stay independent.
 *
 * SECURITY: the token only ever DE-scopes — `BearerAuth` builds the ctx from
 * the token's claims, and the mint resolver already authorized the operator may
 * reach that tenant + holds that role. A window can never see a tenant the
 * operator can't reach.
 */

const WINDOW_TOKEN_PARAM = 'wtoken';

let windowToken: string | null = null;
let initialized = false;

/**
 * Read the wtoken from the current frame's URL exactly once. Kept lazy (rather
 * than reading at import time) so tests can control it via `setWindowToken`
 * before first access, and so SSR/no-window contexts are safe.
 */
function initFromUrl(): void {
  if (initialized) return;
  initialized = true;
  if (typeof window === 'undefined') return;
  const token = new URLSearchParams(window.location.search).get(WINDOW_TOKEN_PARAM);
  if (token && token.trim() !== '') {
    windowToken = token;
  }
}

/**
 * The window token for THIS frame, or null when running as the shell / a normal
 * top-level studio tab (→ cookie auth). Apollo links call this on every request.
 */
export function getWindowToken(): string | null {
  initFromUrl();
  return windowToken;
}

/** True when this frame is a token-authenticated tenant window (bearer mode). */
export function isWindowTokenMode(): boolean {
  return getWindowToken() !== null;
}

/** Test/override hook — set the token explicitly (bypasses URL parsing). */
export function setWindowToken(token: string | null): void {
  initialized = true;
  windowToken = token && token.trim() !== '' ? token : null;
}

/**
 * Build the embedded-studio iframe URL for a tenant window, carrying the minted
 * token. `route` is an optional SPA hash route to land on (window continuity).
 */
export function buildWindowSrc(token: string, route?: string | null): string {
  const base = `/studio/?embed=workspace&${WINDOW_TOKEN_PARAM}=${encodeURIComponent(token)}`;
  const hash = route && route.startsWith('#') ? route : route ? `#${route}` : '';
  return `${base}${hash}`;
}
